import React, { useMemo, useState } from 'react';
import { clsx } from 'clsx';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from '@/shared/hooks/useLocation';
import { Button } from '@/shared/ui/Button';
import { useToast } from '@/shared/ui/Toast';
import { campaignsApi, type AutopilotPlan, type MarketingCampaign } from '../api/campaignsApi';
import { CircleCheckBig, Target, ClipboardList, Zap, Check, Circle } from 'lucide-react';
import { ExtBadge, PNBadge } from './CampaignBadges';
import { ExistingCampaignRow, type ExistingCampaign } from './ExistingCampaignRow';
import { campaignKeys, adsKeys } from '../queryKeys';
import styles from './AutopilotWizard.module.css';

const BUDGET_MIN = 500;
const BUDGET_MAX = 10000;
const BUDGET_STEP = 100;

const CHANNEL_LABELS: Record<string, string> = {
  google_search: 'Google PPC',
  google_pmax: 'Google PMax',
  google_lsa: 'Google LSAs',
  google_display: 'Google Display',
  meta: 'Meta',
  tiktok: 'TikTok',
};

function formatChannel(ch: string): string {
  return CHANNEL_LABELS[ch] ?? ch;
}

interface Props {
  /** Recommended campaigns the wizard will turn into the autopilot plan. */
  recommendedCampaigns: MarketingCampaign[];
  /** External / existing live campaigns the user can keep or pause. */
  existingCampaigns: ExistingCampaign[];
  onCancel: () => void;
  onSwitchToGuided: () => void;
}

type Step = 'budget' | 'existing' | 'allocate' | 'review' | 'done';

/**
 * 5-step Autopilot wizard. Maps directly to the v9 mockup flow:
 *   0 budget       — slider with 7 tiers, goals from strategy
 *   1 existing     — keep / pause each external campaign
 *   2 allocate     — split between existing and new strategy campaigns
 *   3 review       — full plan + "what happens next" timeline
 *   4 done         — confirmation
 */
export function AutopilotWizard({
  recommendedCampaigns,
  existingCampaigns,
  onCancel,
  onSwitchToGuided,
}: Props) {
  const { currentLocation } = useLocation();
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  // Derive initial budget from the sum of strategy-recommended budgets
  const strategyTotal = useMemo(
    () => recommendedCampaigns.reduce((s, c) => s + (c.brief?.monthlyBudget ?? 0), 0),
    [recommendedCampaigns],
  );

  const [step, setStep] = useState<Step>('budget');
  const [totalBudget, setTotalBudget] = useState(() => {
    // Snap strategy total to nearest BUDGET_STEP, clamped within range
    const raw = strategyTotal;
    return Math.max(BUDGET_MIN, Math.min(BUDGET_MAX, Math.round(raw / BUDGET_STEP) * BUDGET_STEP));
  });
  const [keptIds, setKeptIds] = useState<string[]>(existingCampaigns.map((c) => c.id));
  const [existingSplit, setExistingSplit] = useState(existingCampaigns.length > 0 ? 30 : 0);
  const existingAmt = Math.round((totalBudget * existingSplit) / 100);
  const newAmt = totalBudget - existingAmt;

  const planCampaigns = useMemo(() => {
    // Use each campaign's strategy-recommended budget to determine proportional share
    const budgets = recommendedCampaigns.map((c) => c.brief?.monthlyBudget ?? 0);
    const budgetSum = budgets.reduce((s, b) => s + b, 0);
    return recommendedCampaigns.map((c, i) => {
      const share = budgetSum > 0 ? budgets[i] / budgetSum : 1 / recommendedCampaigns.length;
      return {
        rec: c,
        monthlyBudget: Math.round(newAmt * share),
        pct: Math.round(share * 100),
        idx: i,
      };
    });
  }, [recommendedCampaigns, newAmt]);

  const approveMutation = useMutation({
    mutationFn: () => {
      const plan: AutopilotPlan = {
        locationId: currentLocation!.id,
        monthlyAdBudget: totalBudget,
        keepExistingCampaignIds: keptIds,
        campaigns: planCampaigns.map(({ rec, monthlyBudget }) => {
          const brief = (rec.brief ?? {}) as Record<string, unknown> & { _rationale?: string };
          return {
            id: rec.id,
            name: rec.name,
            objective: (rec.objective ?? 'leads') as 'leads' | 'conversions' | 'awareness',
            monthlyBudget,
            channelMix: { google_ppc: 50, google_pmax: 30, meta: 20 },
            aiRationale: brief._rationale ?? '',
            offerHook: (brief.offer as string | undefined) ?? '',
            cta: (brief.cta as string | undefined) ?? '',
            targetAudience: (brief.targetAudience as string | undefined) ?? '',
            keyMessages: (brief.keyMessages as string[] | undefined) ?? [],
            geo: (brief.geo as string | undefined) ?? '',
          };
        }),
      };
      return campaignsApi.autopilot.approve(plan);
    },
    onSuccess: () => {
      setStep('done');
    },
    onError: (err: unknown) => {
      showToast((err as Error).message ?? 'Failed to approve plan', 'error');
    },
  });

  // Progress indicator
  const stepIndex = (['budget', 'existing', 'allocate', 'review', 'done'] as Step[]).indexOf(step);

  return (
    <div className={styles.wizard}>
      <div className={styles.progress}>
        {Array.from({ length: 5 }).map((_, i) => (
          <div
            key={i}
            className={`${styles.progressBar} ${i <= stepIndex ? styles.progressBarOn : ''}`}
          />
        ))}
      </div>

      {step === 'budget' && (
        <div>
          <h2 className={styles.stepTitle}>What's your monthly ad budget?</h2>
          <p className={styles.stepBody}>
            This is your total budget across all campaigns — new and existing.
          </p>
          {existingCampaigns.length > 0 && (
            <p className={styles.hintYellow}>
              If you choose to keep existing campaigns, we'll ask how to split this budget next.
            </p>
          )}

          <div className={styles.budgetCard}>
            <input
              type="range"
              min={BUDGET_MIN}
              max={BUDGET_MAX}
              step={BUDGET_STEP}
              value={totalBudget}
              onChange={(e) => setTotalBudget(Number(e.target.value))}
              className={styles.slider}
            />
            <div className={styles.sliderTicks}>
              <span>${BUDGET_MIN.toLocaleString()}</span>
              <span>${BUDGET_MAX.toLocaleString()}</span>
            </div>
            <div className={styles.budgetValue}>${totalBudget.toLocaleString()}/mo</div>
          </div>

          {recommendedCampaigns.length > 0 && (
            <div className={styles.goalsSection}>
              <div className={styles.goalsTitle}>Goals from your strategy</div>
              <div className={styles.goalsCard}>
                {recommendedCampaigns.map((c) => {
                  const brief = (c.brief ?? {}) as { _strategyTitle?: string };
                  return (
                    <div key={c.id} className={styles.goalRow}>
                      <span className={styles.goalText}>{c.name}</span>
                      {brief._strategyTitle && (
                        <span
                          className={clsx(styles.pill, styles.pillPurple, styles.pillUppercase)}
                        >
                          <Target size={11} aria-hidden /> {brief._strategyTitle}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className={styles.actions}>
            <Button variant="tertiary" size="sm" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={() => setStep(existingCampaigns.length > 0 ? 'existing' : 'review')}
            >
              Next →
            </Button>
          </div>
        </div>
      )}

      {step === 'existing' && (
        <div>
          <h2 className={styles.stepTitle}>Your existing campaigns</h2>
          <p className={styles.stepBody}>These were running before. Keep or pause each one.</p>

          {existingCampaigns.map((c) => (
            <ExistingCampaignRow
              key={c.id}
              campaign={c}
              kept={keptIds.includes(c.id)}
              onKeep={() => setKeptIds((prev) => Array.from(new Set([...prev, c.id])))}
              onPause={() => setKeptIds((prev) => prev.filter((id) => id !== c.id))}
            />
          ))}

          <div className={styles.actions}>
            <Button variant="tertiary" size="sm" onClick={() => setStep('budget')}>
              ← Back
            </Button>
            <Button size="sm" onClick={() => setStep(keptIds.length > 0 ? 'allocate' : 'review')}>
              Next →
            </Button>
          </div>
        </div>
      )}

      {step === 'allocate' && (
        <div>
          <h2 className={styles.stepTitle}>Budget allocation</h2>
          <p className={styles.stepBody}>
            How should we split your ${totalBudget.toLocaleString()}/mo between existing and new
            campaigns?
          </p>
          <p className={styles.hint}>
            We recommend allocating 70%+ to new strategy campaigns for maximum growth impact.
          </p>

          <div className={styles.allocCard}>
            <div className={styles.allocHeader}>
              <span className={styles.allocExt}>Existing: {existingSplit}%</span>
              <span className={styles.allocNew}>New: {100 - existingSplit}%</span>
            </div>
            <input
              type="range"
              min={0}
              max={80}
              value={existingSplit}
              onChange={(e) => setExistingSplit(Number(e.target.value))}
              className={styles.slider}
            />
            <div className={styles.allocSplit}>
              <div className={styles.allocBox} data-variant="external">
                <div className={styles.allocBoxLabel}>
                  <ClipboardList size={14} aria-hidden /> Existing campaigns
                </div>
                <div className={styles.allocBoxValue}>${existingAmt.toLocaleString()}/mo</div>
                {existingCampaigns
                  .filter((c) => keptIds.includes(c.id))
                  .map((c, i, arr) => {
                    const share = arr.length > 0 ? 1 / arr.length : 0;
                    return (
                      <div key={c.id} className={styles.allocBreakdown}>
                        {c.name}: ${Math.round(existingAmt * share).toLocaleString()}/mo
                      </div>
                    );
                  })}
              </div>
              <div className={styles.allocBox} data-variant="pn">
                <div className={styles.allocBoxLabel}>
                  <Zap size={14} aria-hidden /> New strategy campaigns
                </div>
                <div className={styles.allocBoxValue}>${newAmt.toLocaleString()}/mo</div>
                {planCampaigns.map(({ rec, monthlyBudget }) => (
                  <div key={rec.id} className={styles.allocBreakdown}>
                    {rec.name}: ${monthlyBudget.toLocaleString()}/mo
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className={styles.actions}>
            <Button variant="tertiary" size="sm" onClick={() => setStep('existing')}>
              ← Back
            </Button>
            <Button size="sm" onClick={() => setStep('review')}>
              See plan →
            </Button>
          </div>
        </div>
      )}

      {step === 'review' && (
        <div>
          <h2 className={styles.stepTitle}>
            Here's our plan for ${totalBudget.toLocaleString()}/mo
          </h2>
          <p className={styles.stepBody}>
            We'll handle channels, targeting, creative, and optimization.
          </p>

          <div className={styles.sectionLabel}>
            <Zap size={14} aria-hidden /> New strategy campaigns — ${newAmt.toLocaleString()}/mo
          </div>
          {planCampaigns.map(({ rec, monthlyBudget, pct }) => {
            const brief = (rec.brief ?? {}) as {
              _rationale?: string;
              _strategyTitle?: string;
              platforms?: string[];
            };
            return (
              <div key={rec.id} className={styles.planCard}>
                <div>
                  <div className={styles.planHeader}>
                    <span className={styles.planName}>{rec.name}</span>
                    <PNBadge />
                    {brief._strategyTitle && (
                      <span className={clsx(styles.pill, styles.pillPurple, styles.pillUppercase)}>
                        <Target size={11} aria-hidden /> {brief._strategyTitle}
                      </span>
                    )}
                  </div>
                  {brief._rationale && <div className={styles.planBody}>{brief._rationale}</div>}
                  {brief.platforms && brief.platforms.length > 0 && (
                    <div className={styles.channelChips}>
                      {brief.platforms.map((ch) => (
                        <span key={ch} className={styles.channelChip}>
                          <Circle size={8} fill="currentColor" aria-hidden /> {formatChannel(ch)}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className={styles.planBudget}>
                  <div className={styles.planBudgetValue}>${monthlyBudget.toLocaleString()}/mo</div>
                  <div className={styles.planBudgetPct}>{pct}%</div>
                </div>
              </div>
            );
          })}

          {keptIds.length > 0 && (
            <>
              <div className={styles.sectionLabelExt}>
                <ClipboardList size={14} aria-hidden /> Existing campaigns — $
                {existingAmt.toLocaleString()}/mo
              </div>
              {existingCampaigns
                .filter((c) => keptIds.includes(c.id))
                .map((c) => (
                  <div key={c.id} className={styles.planCardExt}>
                    <div className={styles.planHeader}>
                      <span className={styles.planName}>{c.name}</span>
                      {c.source === 'pn-ai' ? <PNBadge /> : <ExtBadge />}
                    </div>
                  </div>
                ))}
            </>
          )}

          <div className={styles.timelineCard}>
            <div className={styles.timelineTitle}>What happens next</div>
            {[
              ['Today', 'You approve this plan'],
              ['Within 24 hours', 'Our team finalizes ad creative'],
              ['Within 48 hours', 'Campaigns go live'],
              ['Weekly', 'Performance updates in your dashboard'],
            ].map(([when, what]) => (
              <div key={when} className={styles.timelineRow}>
                <span className={styles.timelineWhen}>{when}</span>
                <span className={styles.timelineWhat}>{what}</span>
              </div>
            ))}
          </div>

          <div className={styles.actions}>
            <Button
              variant="tertiary"
              size="sm"
              onClick={() => setStep(keptIds.length > 0 ? 'allocate' : 'budget')}
            >
              ← Adjust
            </Button>
            <button type="button" className={styles.controlLink} onClick={onSwitchToGuided}>
              I want more control
            </button>
            <Button
              size="sm"
              onClick={() => approveMutation.mutate()}
              isLoading={approveMutation.isPending}
            >
              Approve plan <Check size={14} aria-hidden />
            </Button>
          </div>
        </div>
      )}

      {step === 'done' && (
        <div className={styles.done}>
          <div className={styles.doneIcon}>
            <CircleCheckBig size={44} strokeWidth={1.5} color="var(--success)" />
          </div>
          <div className={styles.doneTitle}>Plan approved!</div>
          <p className={styles.doneBody}>
            Our team is setting up your campaigns. You'll be notified when they go live — typically
            within 48 hours.
          </p>
          <Button
            onClick={() => {
              queryClient.invalidateQueries({ queryKey: campaignKeys.marketing() });
              queryClient.invalidateQueries({ queryKey: adsKeys.auditAll() });
              onCancel();
            }}
          >
            Go to Overview →
          </Button>
        </div>
      )}
    </div>
  );
}
